import { useState } from 'react';
import { Card, Tag, Button, Space, Spinner, Tooltip } from '../../ui';
import { MapPin, UserPlus, AlertTriangle, RefreshCw, Check, Eye, ChevronRight } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../../api/client';
import { RoleBadge } from '../../components/RoleBadge';

/**
 * A place the server counts as having no sales person. `below` is how many further places inside it
 * are unattended for the same reason — one assignment here clears all of them, so they get one row
 * rather than `below + 1`. `inside` is those places, revealed on demand: the roll-up gives the
 * cheapest fix, not necessarily the right one, and staffing usually belongs on the districts.
 */
export interface UnassignedPlace {
  id: string; code: string; name: string; level: string;
  below: number;
  /** The unattended places within this gap — same array `below` is counted from. */
  inside: Array<{ id: string; code: string; name: string; level: string }>;
}

const LEVEL_TONE: Record<string, 'blue' | 'cyan' | 'green'> = { state: 'blue', district: 'cyan', beat: 'green' };

/**
 * Who is attached to a flagged place without working it — a Sales Head watching it, or watching a
 * district or state above it. Named, because "Overseen, not worked" told the reader a state but hid
 * the one fact that makes it actionable: WHO to talk to, or whose ☆ to click.
 */
export interface PlaceOversight {
  name: string;
  roleCode: string;
  /** How many more people are attached beyond `name`. */
  others: number;
  /** The ancestor territory the binding actually sits on, when it is not on the place itself. */
  via?: string | null;
  /** The full explanation, for the hover. */
  why: string;
}

/** How many rows before the list folds behind a "show the rest" button. */
const FIRST_PAGE = 8;

/**
 * "Needs a sales person" — the worklist behind the Territories badge.
 *
 * The badge on the Coverage tab is `territoriesUnassigned + coverageNeeded`. `coverageNeeded` already
 * has a panel (the amber alert in CoverageManagerCard); `territoriesUnassigned` had NOTHING — the
 * number was real but nothing on the page was that number, so the only way to find what it counted
 * was to eyeball an expanded tree. This card is that missing half.
 *
 * It reads `/territory-assignments/unassigned`, the SAME server call `unassignedCount` counts (and
 * with the same Sales-Head scoping), so this header IS the badge's figure by construction rather than
 * a second count recomputed from the tree, which could drift. Since that endpoint now returns every
 * unworked STATE and DISTRICT as well as beats, this list and the tree's red marks are the same set —
 * they used to disagree, which is how a screen of red districts sat under a headline of zero.
 */
export function UnassignedPlacesCard({ pathOf, reasonOf, onAssign, canAssign }: {
  /** "Chhattisgarh › Balod" for a place id — the name alone doesn't say where it sits. */
  pathOf: (id: string) => string;
  /** Who is attached to this place without working it, or null when nobody is. */
  reasonOf: (id: string) => PlaceOversight | null;
  onAssign: (place: UnassignedPlace) => void;
  canAssign: boolean;
}) {
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    // Deliberately a CHILD of the ['territory-coverage'] key: React Query invalidates by prefix, so
    // every existing `invalidateQueries(['territory-coverage'])` — assign, remove, ⭐, transfer, split,
    // merge, SH transfer — refetches this list too, and any future one does as well. Naming it
    // separately would have meant patching eight call sites and hoping the ninth remembers.
    queryKey: ['territory-coverage', 'unassigned'],
    queryFn: () => request<UnassignedPlace[]>({ url: '/territory-assignments/unassigned' }),
  });

  // A failed load is NOT "everything is assigned" — saying so would quietly retire real work.
  if (isError) {
    return (
      <Card title={<Space><AlertTriangle size={16} />Needs a sales person</Space>} style={{ marginBottom: 14 }}>
        <div className="flex flex-wrap items-center gap-3 text-sm text-danger-500">
          <span>Couldn’t load the places without a sales person{(error as any)?.response?.data?.error?.message ? ` — ${(error as any).response.data.error.message}` : '.'}</span>
          <Button size="sm" icon={<RefreshCw size={16} />} loading={isFetching} onClick={() => refetch()}>Retry</Button>
        </div>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <Card title={<Space><AlertTriangle size={16} />Needs a sales person</Space>} style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-2 py-2 text-muted"><Spinner /> Loading…</div>
      </Card>
    );
  }

  const beats = data ?? [];

  if (!beats.length) {
    return (
      <Card title={<Space><Check size={16} />Needs a sales person</Space>} style={{ marginBottom: 14 }}>
        <div className="flex items-center gap-2 py-1 text-sm text-muted">
          <Tag tone="green">0</Tag> Every state, district and beat has someone working it.
        </div>
      </Card>
    );
  }

  const shown = showAll ? beats : beats.slice(0, FIRST_PAGE);

  return (
    <Card
      title={
        <Space>
          <AlertTriangle size={16} />
          {beats.length} {beats.length === 1 ? 'place has' : 'places have'} no sales person
        </Space>
      }
      style={{ marginBottom: 14 }}
    >
      <p className="mb-2 mt-0 text-sm text-muted">
        One row per gap, at the highest place it starts. Assigning there covers everything inside it —
        or open <b className="font-medium">+N inside</b> to put someone on a district or beat instead.
        A Sales Person can only hold a district or a beat, so a state row needs a Sales Head.
      </p>

      <ul aria-label="Places with no sales person" className="m-0 list-none divide-y divide-line p-0">
        {shown.map((b) => {
          const path = pathOf(String(b.id));
          const seen = reasonOf(String(b.id));
          const open = expanded.has(String(b.id));
          return (
            <li key={b.id} className="py-2">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <Tag tone={LEVEL_TONE[b.level] ?? 'neutral'} className="shrink-0 text-[10px] uppercase tracking-wide">{b.level}</Tag>
              {/* The code is an identifier, the name is what a person recognises — neither needs bold.
                  Weight here only separates the two; the row's emphasis belongs to the Assign button. */}
              <span className="shrink-0 text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{b.code}</span>
              <span className="font-medium text-content">{b.name}</span>
              {path && (
                <span className="inline-flex min-w-0 items-center gap-1 text-sm text-subtle">
                  <MapPin size={13} className="shrink-0" />
                  <span className="truncate">{path}</span>
                </span>
              )}
              {b.below > 0 && (
                <Tooltip label={`${b.below} ${b.below === 1 ? 'place' : 'places'} inside ${b.name} ${b.below === 1 ? 'has' : 'have'} nobody either. Assigning at ${b.name} covers all of them — open this to put someone on one of them instead.`}>
                  <button
                    type="button"
                    onClick={() => setExpanded((s) => {
                      const n = new Set(s);
                      if (n.has(String(b.id))) n.delete(String(b.id)); else n.add(String(b.id));
                      return n;
                    })}
                    aria-expanded={open}
                    className="inline-flex items-center gap-1 rounded-pill bg-surface-3 px-2 py-0.5 type-caption-strong text-muted transition-colors hover:bg-surface-2 hover:text-content"
                  >
                    <ChevronRight size={12} className={open ? 'rotate-90 transition-transform' : 'transition-transform'} />
                    +{b.below} inside
                  </button>
                </Tooltip>
              )}
              {seen && (
                <Tooltip label={seen.why}>
                  <span className="inline-flex min-w-0 items-center gap-1.5 text-sm text-muted">
                    <Eye size={13} className="shrink-0" />
                    <span className="truncate">
                      {seen.name}{seen.others > 0 ? ` +${seen.others}` : ''}
                      {seen.via ? ` · oversees ${seen.via}` : ` · oversees this ${b.level}`}
                    </span>
                    <RoleBadge role={seen.roleCode} />
                  </span>
                </Tooltip>
              )}
              {canAssign && (
                <Button
                  className="ml-auto shrink-0"
                  size="sm"
                  variant="primary"
                  icon={<UserPlus size={16} />}
                  onClick={() => onAssign(b)}
                >
                  Assign
                </Button>
              )}
            </div>

            {/* The places this gap covers, each assignable on its own — so "one action" stays an offer
                rather than the only door. Indented under their top, which is where the gap starts. */}
            {open && (
              <ul aria-label={`Places inside ${b.name}`} className="m-0 mt-1 list-none border-l border-line py-0 pl-3">
                {b.inside.map((c) => (
                  <li key={c.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-1.5">
                    <Tag tone={LEVEL_TONE[c.level] ?? 'neutral'} className="shrink-0 text-[10px] uppercase tracking-wide">{c.level}</Tag>
                    <span className="shrink-0 text-muted" style={{ fontVariantNumeric: 'tabular-nums' }}>{c.code}</span>
                    <span className="text-content">{c.name}</span>
                    {canAssign && (
                      <Button
                        className="ml-auto shrink-0"
                        size="sm"
                        variant="default"
                        icon={<UserPlus size={15} />}
                        onClick={() => onAssign({ ...c, below: 0, inside: [] })}
                      >
                        Assign
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
            </li>
          );
        })}
      </ul>

      {beats.length > FIRST_PAGE && (
        <Button size="sm" variant="default" className="mt-2" onClick={() => setShowAll(!showAll)}>
          {showAll ? 'Show fewer' : `Show the other ${beats.length - FIRST_PAGE}`}
        </Button>
      )}
    </Card>
  );
}
